import { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Form, Modal } from 'antd';
import { TableRowActionsMenu } from '~/app/components/common/TableRowActionsMenu/TableRowActionsMenu';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import type { PolygonPerimeter } from '@evimo/types';
import type { SiteLookup } from '~/app/services/site/site.service';
import type { AddressAutocompleteSelection } from '~/app/hooks/site/useSiteAddressAutocomplete';
import {
  useSiteManagement,
  type SiteFormValues,
} from '~/app/hooks/site/useSiteManagement';
import { SiteManagementView } from './SiteManagementView';

export function SiteManagement() {
  const { t } = useTranslation('sites');
  const [form] = Form.useForm<SiteFormValues>();
  const selectedProvinceCode = Form.useWatch('provinceCode', form);
  const selectedCityCode = Form.useWatch('cityCode', form);
  const selectedAddress = Form.useWatch('address', form);
  const selectedLatitude = Form.useWatch('latitude', form);
  const selectedLongitude = Form.useWatch('longitude', form);
  const selectedPerimeter = Form.useWatch('perimeter', form);
  const {
    cities,
    editingSite,
    error,
    findCityByCode,
    findProvinceByCode,
    isDeleting,
    isFetching,
    isLoading,
    isModalOpen,
    isSubmitting,
    page,
    pagination,
    provinces,
    searchValue,
    citySearchValue,
    provinceSearchValue,
    closeModal,
    deleteSite,
    openCreateModal,
    openEditModal,
    refetch,
    resolveAddressSelection,
    setCitySearchValue,
    setProvinceSearchValue,
    submitSite,
    sites,
    updatePage,
    updateSearchValue,
  } = useSiteManagement(selectedProvinceCode);

  useEffect(() => {
    if (!isModalOpen) {
      form.resetFields();
      return;
    }

    form.setFieldsValue({
      name: editingSite?.name ?? '',
      address: editingSite?.address ?? undefined,
      postalCode: editingSite?.postalCode ?? undefined,
      cityCode: editingSite?.city?.code ?? undefined,
      provinceCode: editingSite?.province?.code ?? undefined,
      latitude:
        editingSite?.latitude === null || editingSite?.latitude === undefined
          ? undefined
          : String(editingSite.latitude),
      longitude:
        editingSite?.longitude === null || editingSite?.longitude === undefined
          ? undefined
          : String(editingSite.longitude),
      perimeter: editingSite?.perimeter ?? undefined,
    });
  }, [editingSite, form, isModalOpen]);

  const handleDeleteSite = (site: SiteLookup): void => {
    Modal.confirm({
      title: t('actions.deleteSite', 'Delete site'),
      content: t(
        'messages.deleteSiteConfirm',
        'Delete this site? Linked zones and assets may also be removed.',
      ),
      okText: t('actions.delete', 'Delete'),
      cancelText: t('actions.cancel', 'Cancel'),
      okButtonProps: { danger: true, loading: isDeleting },
      onOk: () => deleteSite(site.id),
    });
  };

  const provinceOptions = provinces.map((province) => ({
    label: province.name,
    value: province.code,
  }));

  const cityOptions = cities.map((city) => ({
    label: city.name,
    value: city.code,
  }));

  const selectedProvince = findProvinceByCode(selectedProvinceCode);
  const selectedCity = findCityByCode(selectedCityCode);

  const handleCoordinatesChange = useCallback(
    (coordinates: { latitude: string; longitude: string }) => {
      form.setFieldsValue({
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        perimeter: undefined,
      });
    },
    [form],
  );

  const handlePerimeterChange = useCallback(
    (perimeter: PolygonPerimeter | null) => {
      form.setFieldValue('perimeter', perimeter);
    },
    [form],
  );

  const handleAddressSelection = useCallback(
    async (selection: AddressAutocompleteSelection) => {
      const resolvedLocation = await resolveAddressSelection(selection);

      setProvinceSearchValue(resolvedLocation.provinceName ?? '');
      setCitySearchValue(resolvedLocation.cityName ?? '');

      form.setFieldsValue({
        address: selection.address,
        postalCode: selection.postalCode,
        cityCode: resolvedLocation.cityCode,
        provinceCode: resolvedLocation.provinceCode,
        latitude: selection.latitude.toFixed(6),
        longitude: selection.longitude.toFixed(6),
        perimeter: undefined,
      });
    },
    [form, resolveAddressSelection, setCitySearchValue, setProvinceSearchValue],
  );

  const handleLocationSelectionFromMap = useCallback(
    async (selection: {
      address?: string;
      cityName?: string;
      provinceName?: string;
      postalCode?: string;
      latitude: number;
      longitude: number;
    }) => {
      await handleAddressSelection({
        address: selection.address ?? form.getFieldValue('address') ?? '',
        cityName: selection.cityName,
        provinceName: selection.provinceName,
        postalCode: selection.postalCode,
        latitude: selection.latitude,
        longitude: selection.longitude,
      });
    },
    [form, handleAddressSelection],
  );

  const columns: ColumnsType<SiteLookup> = [
    {
      title: t('table.name', 'Site name'),
      dataIndex: 'name',
      key: 'name',
      render: (_value, site) => (
        <Link to={`/sites/${site.id}`}>{site.name}</Link>
      ),
    },
    {
      title: t('table.address', 'Address'),
      dataIndex: 'address',
      key: 'address',
      render: (value: string | null) => value || '-',
    },
    {
      title: t('table.postalCode', 'Postal code'),
      dataIndex: 'postalCode',
      key: 'postalCode',
      render: (value: string | null) => value || '-',
    },
    {
      title: t('table.province', 'Province'),
      dataIndex: ['province', 'name'],
      key: 'province',
      render: (_value, site) => site.province?.name || '-',
    },
    {
      title: t('table.city', 'City'),
      dataIndex: ['city', 'name'],
      key: 'city',
      render: (_value, site) => site.city?.name || '-',
    },
    {
      title: t('table.latitude', 'Latitude'),
      dataIndex: 'latitude',
      key: 'latitude',
      render: (value: number | null) => value ?? '-',
    },
    {
      title: t('table.longitude', 'Longitude'),
      dataIndex: 'longitude',
      key: 'longitude',
      render: (value: number | null) => value ?? '-',
    },
    {
      title: t('table.actions', 'Actions'),
      key: 'actions',
      render: (_value, site) => (
        <TableRowActionsMenu
          ariaLabel={t('actions.more', 'More actions')}
          items={[
            {
              key: 'view-map',
              label: t('actions.viewMap', 'View site map'),
              onClick: () => {
                window.location.assign(`/sites/${site.id}`);
              },
            },
            {
              key: 'edit',
              label: t('actions.edit', 'Edit'),
              onClick: () => openEditModal(site),
            },
            {
              key: 'delete',
              label: t('actions.delete', 'Delete'),
              danger: true,
              onClick: () => handleDeleteSite(site),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <SiteManagementView
      cities={cities}
      cityOptions={cityOptions}
      citySearchValue={citySearchValue}
      columns={columns}
      editingSite={editingSite}
      error={error}
      form={form}
      isFetching={isFetching}
      isLoading={isLoading}
      isModalOpen={isModalOpen}
      isSubmitting={isSubmitting}
      onAddressChange={(value) => {
        form.setFieldValue('address', value);
      }}
      onAddressSelect={handleAddressSelection}
      onCityChange={(value) => {
        const hasChanged = value !== form.getFieldValue('cityCode');
        const city = findCityByCode(value);

        setCitySearchValue(city?.name ?? '');

        if (hasChanged) {
          form.setFieldsValue({
            latitude: undefined,
            longitude: undefined,
            perimeter: undefined,
          });
        }

        if ('provinceCode' in (city ?? {})) {
          form.setFieldValue('provinceCode', city?.provinceCode);
          setProvinceSearchValue('');
        }
      }}
      onCitySearchChange={setCitySearchValue}
      onCloseModal={closeModal}
      onCoordinatesChange={handleCoordinatesChange}
      onLocationSelect={handleLocationSelectionFromMap}
      onPerimeterChangeAction={handlePerimeterChange}
      onOpenCreateModal={openCreateModal}
      onPageChange={updatePage}
      onProvinceChange={(value) => {
        const hasChanged = value !== form.getFieldValue('provinceCode');
        const province = findProvinceByCode(value);
        const shouldClearCity =
          !value ||
          (selectedCity?.provinceCode !== undefined &&
            selectedCity.provinceCode !== value);

        setProvinceSearchValue(province?.name ?? '');
        setCitySearchValue(shouldClearCity ? '' : (selectedCity?.name ?? ''));

        if (hasChanged) {
          form.setFieldsValue({
            cityCode: shouldClearCity
              ? undefined
              : form.getFieldValue('cityCode'),
            latitude: undefined,
            longitude: undefined,
            perimeter: undefined,
          });
        }
      }}
      onProvinceSearchChange={setProvinceSearchValue}
      onRefresh={() => {
        void refetch();
      }}
      onSearchChange={updateSearchValue}
      onSubmitSite={(values) =>
        submitSite({
          ...values,
          perimeter: selectedPerimeter ?? null,
        })
      }
      page={page}
      pagination={pagination}
      provinces={provinces}
      provinceOptions={provinceOptions}
      provinceSearchValue={provinceSearchValue}
      searchValue={searchValue}
      selectedAddress={selectedAddress}
      selectedCity={selectedCity}
      selectedLatitude={selectedLatitude}
      selectedLongitude={selectedLongitude}
      selectedPerimeter={selectedPerimeter}
      selectedProvince={selectedProvince}
      sites={sites}
    />
  );
}
